import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lightbulb, Microscope, Users, Palette, Dumbbell, Leaf, Heart, CheckCircle, Award } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const approaches = [
  { title: "Play-Way Method", desc: "Learning through play, puppet shows, sandbox exploration, and games to keep kids excited." },
  { title: "Montessori-Inspired Concepts", desc: "Sensory learning materials that help children discover concepts at their own individual pace." },
  { title: "Activity-Based Learning", desc: "Joyful hands-on activities that convert complex abstract ideas into concrete understanding." },
  { title: "Experiential Learning", desc: "Direct interactions, school garden projects, and group experiments that make learning real." },
  { title: "Interactive Teaching", desc: "Conversations, smart boards, and collaborative circles that build speaking confidence." },
  { title: "Creative Expression", desc: "Scribbling, painting, modeling clay, and music to stimulate early imagination." },
  { title: "Language Enrichment", desc: "Story sessions, voice modulations, phonics, and vocabulary blocks for early reading." },
  { title: "Early STEM Introduction", desc: "Matching puzzles, block arrangements, and sorting games for mathematical logic." },
  { title: "Physical Development", desc: "Structured balancing tracks, relays, and outdoor play to build gross motor skills." },
  { title: "Social & Emotional Learning", desc: "Circle time, group work, sharing toys, and learning kindness in everyday interactions." }
];

const learningAreas = [
  { icon: BookOpen, title: "Language & Communication", desc: "Storytelling, phonics, vocabulary building, listening, expression, and speaking confidence.", color: "#FDE8E9", ic: "#C8161D" },
  { icon: Lightbulb, title: "Numeracy Skills", desc: "Counting, patterns, shapes, sorting, size comparisons, basic mathematics, and logical reasoning.", color: "#FEF9C3", ic: "#ca8a04" },
  { icon: Palette, title: "Creative Arts", desc: "Drawing, painting, craft, dance, music, role-play, clay modeling, and imaginative expression.", color: "#FCE7F3", ic: "#DB2777" },
  { icon: Dumbbell, title: "Physical Development", desc: "Yoga, movement, outdoor play, balance, coordination, fine-motor crafts, and healthy habits.", color: "#F3F9E2", ic: "#9DC41E" },
  { icon: Heart, title: "Social & Emotional Growth", desc: "Teamwork, kindness, confidence, independence, emotional awareness, sharing, and relationship building.", color: "#E0F2FE", ic: "#0284c7" },
  { icon: Leaf, title: "Environmental Awareness", desc: "Learning about nature, animals, plants, seasons, community helpers, and everyday surroundings.", color: "#F0FFF4", ic: "#16A34A" },
  { icon: Microscope, title: "Early Science & Discovery", desc: "Observation, simple child-safe experiments, building blocks, and introductory STEM activities.", color: "#E0F2FE", ic: "#0284c7" }
];

const experiences = [
  "Story-based lessons that spark creative thinking",
  "Theme-based classrooms decorated with child creations",
  "Hands-on learning materials and non-toxic clay",
  "Collaborative group projects that teach coordination",
  "Nature exploration and school gardening",
  "Rhythmic music and expressive movement breaks",
  "Role-play and theatrical dressing sessions",
  "Creative problem-solving games and block building",
  "Classroom responsibilities like watering plants"
];

export default function Academics() {
  return (
    <div>
      <PageHero 
        title="A Joyful Curriculum for Whole-Child Development" 
        subtitle="Our curriculum combines play, exploration, creativity, language, movement, social interaction, and age-appropriate academic preparation." 
        breadcrumb="Academics" 
      />

      {/* Curriculum Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Our Methods</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Our Curriculum Approach</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">We combine the best early education philosophies to nurture all areas of your child's developmental journey.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {approaches.map((app, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="cartoon-card bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <h3 className="font-nunito font-extrabold text-lg text-[#1A1A1A] mb-3">{app.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{app.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Learning Areas */}
      <section className="py-20 bg-[#F8FAFC] polka-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Core Pillars</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Key Learning Areas</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Our daily schedules cover a comprehensive set of growth domains to ensure well-rounded development.</p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {learningAreas.map((area, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 flex gap-4 items-start"
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: area.color }}>
                  <area.icon size={26} style={{ color: area.ic }} />
                </div>
                <div>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-2">{area.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{area.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="white" bg="#F8FAFC" />

      {/* Learning through Experience */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={viewportOpts} transition={{ duration: 0.6 }}>
              <span className="section-label">Hands-On Growth</span>
              <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3 mb-6">Learning Through Experience</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Preschool children do not learn from lectures. They learn by doing, smelling, feeling, moving, and connecting. Our everyday activities are deliberately designed to provide experiential learning moments.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {experiences.map((exp, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <CheckCircle className="text-[#9DC41E] flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600 text-sm">{exp}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="bg-[#F3F9E2] rounded-3rem p-10 border border-[#9DC41E]/20"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={viewportOpts}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 bg-[#9DC41E] rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg">
                <Award size={32} />
              </div>
              <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-4">Progress and Parent Updates</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every child develops at a unique pace. Our educators carefully observe and record participation, communication, confidence, creativity, social interaction, motor development, and academic readiness.
              </p>
              <h4 className="font-nunito font-bold text-lg text-[#1A1A1A] mb-3">Communication touchpoints include:</h4>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9DC41E] rounded-full" /> Detailed term progress reports
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9DC41E] rounded-full" /> Regular parent-teacher meetings (PTMs)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9DC41E] rounded-full" /> Informal developmental feedback sessions
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9DC41E] rounded-full" /> Photo and activity updates via WhatsApp / school app
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
